/**
 * Daily Random Utility
 * Provides consistent randomization that changes daily
 * Used to rotate content on the homepage while maintaining consistency throughout the day
 */

/**
 * Generate a seed based on today's date
 * @returns {number} A seed number that changes daily
 */
export const getDailyRandomSeed = () => {
  const today = new Date();
  // Create a date string in YYYY-MM-DD format
  const dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
  
  // Generate a hash from the date string
  let seed = 0;
  for (let i = 0; i < dateString.length; i++) {
    seed = ((seed << 5) - seed) + dateString.charCodeAt(i);
    seed = seed & seed; // Convert to 32-bit integer
  }
  
  return Math.abs(seed);
};

/**
 * Seeded random number generator
 * @param {number} seed - The seed value
 * @returns {function} A random function that uses the seed
 */
const createSeededRandom = (seed) => {
  let currentSeed = seed;
  
  return () => {
    // Linear congruential generator
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    return currentSeed / 233280;
  };
};

/**
 * Shuffle an array using a daily seed for consistency
 * @param {Array} array - The array to shuffle
 * @param {number} [customSeed] - Optional custom seed (defaults to daily seed)
 * @returns {Array} A new shuffled array
 */
export const dailyShuffle = (array, customSeed = null) => {
  if (!array || array.length === 0) return array;
  
  const seed = customSeed !== null ? customSeed : getDailyRandomSeed();
  const random = createSeededRandom(seed);
  const shuffled = [...array];
  
  // Fisher-Yates shuffle with seeded random
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

/**
 * Get a random subset of items from an array using daily seed
 * @param {Array} array - The source array
 * @param {number} count - Number of items to select
 * @param {number} [customSeed] - Optional custom seed
 * @returns {Array} Random subset of items
 */
export const getDailyRandomSubset = (array, count, customSeed = null) => {
  if (!array || array.length === 0) return [];
  if (count >= array.length) return dailyShuffle(array, customSeed);
  
  const shuffled = dailyShuffle(array, customSeed);
  return shuffled.slice(0, count);
};

/**
 * Get a diverse selection mixing recent and random items
 * @param {Array} array - The source array
 * @param {number} totalCount - Total number of items to return
 * @param {number} recentCount - How many recent items to include
 * @param {string} dateField - The field name containing the date
 * @returns {Array} Mixed selection of recent and random items
 */
export const getDailyMixedSelection = (array, totalCount, recentCount = 2, dateField = 'published_at') => {
  if (!array || array.length === 0) return [];
  
  // Sort by date to get recent items
  const sorted = [...array].sort((a, b) => {
    const dateA = new Date(a[dateField] || 0);
    const dateB = new Date(b[dateField] || 0);
    return dateB - dateA;
  });
  
  // Take some recent items
  const recent = sorted.slice(0, Math.min(recentCount, totalCount));
  
  // Get remaining items (exclude the recent ones)
  const remaining = sorted.slice(recentCount);
  
  // Randomly select from remaining to fill up to totalCount
  const randomCount = totalCount - recent.length;
  const randomItems = getDailyRandomSubset(remaining, randomCount);
  
  // Combine and shuffle the final selection
  return dailyShuffle([...recent, ...randomItems]);
};

/**
 * Debug helper to show what content will appear on different days
 * @param {Array} array - The array to test
 * @param {number} count - Items per day
 * @param {number} days - Number of days to simulate
 */
export const previewDailyRotation = (array, count, days = 7) => {
  console.log('=== Daily Content Rotation Preview ===');
  const today = new Date();
  
  for (let d = 0; d < days; d++) {
    const testDate = new Date(today);
    testDate.setDate(today.getDate() + d);
    
    // Create seed for this date
    const dateString = `${testDate.getFullYear()}-${testDate.getMonth() + 1}-${testDate.getDate()}`;
    let seed = 0;
    for (let i = 0; i < dateString.length; i++) {
      seed = ((seed << 5) - seed) + dateString.charCodeAt(i);
      seed = seed & seed;
    }
    seed = Math.abs(seed);
    
    const selection = getDailyRandomSubset(array, count, seed);
    console.log(`Day ${d} (${dateString}):`, selection.map(item => item.title || item.name).slice(0, 3));
  }
};